import { Link } from 'react-router-dom';
import { MapPin, Clock, MessageSquare, Eye, ImageIcon } from 'lucide-react';
import type { AdListItem } from '@nimbazaar/store';

export interface AdCardProps {
  id: string;
  slug: string;
  title: string;
  budget: string;
  category: string;
  location?: string;
  postedTime: string;
  bids: number;
  views: number;
  image?: string;
  urgent?: boolean;
  fullWidth?: boolean;
}

function formatBudget(min?: number, max?: number): string {
  const format = (n: number) => {
    if (n >= 10000000) {
      return `Rs. ${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Crore`;
    } else if (n >= 100000) {
      return `Rs. ${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} Lac`;
    } else if (n >= 1000) {
      return `Rs. ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)} Thousand`;
    }
    return `Rs. ${n.toLocaleString()}`;
  };

  if (min && max) {
    return `${format(min)} - ${format(max)}`;
  } else if (min) {
    return `From ${format(min)}`;
  } else if (max) {
    return `Up to ${format(max)}`;
  }
  return 'Budget not specified';
}

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

export function apiAdToCardProps(ad: AdListItem): AdCardProps {
  return {
    id: ad.id,
    slug: ad.slug,
    title: ad.title,
    budget: formatBudget(ad.minBudget, ad.maxBudget),
    category: ad.category.name,
    location: ad.location,
    postedTime: formatRelativeTime(ad.createdAt),
    bids: ad.bidCount,
    views: ad.viewCount,
    image: ad.images?.[0]?.url,
  };
}

export function AdCard({
  slug,
  title,
  budget,
  category,
  location,
  postedTime,
  bids,
  views,
  image,
  urgent,
  fullWidth,
}: AdCardProps) {
  return (
    <Link
      to={`/ad/${slug}`}
      className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden group block ${
        fullWidth ? 'w-full' : 'min-w-[160px] max-w-[180px] sm:min-w-[220px] sm:max-w-[240px]'
      }`}
    >
      {/* Image */}
      <div className="relative h-36 sm:h-44 bg-gray-100 overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-300" />
          </div>
        )}
        {urgent && (
          <span className="absolute top-2 left-2 bg-secondary-500 text-white text-xs font-semibold px-2 py-1 rounded">
            Urgent
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        {/* Title */}
        <h4 className="font-semibold text-gray-900 text-xs sm:text-sm line-clamp-2 h-8 sm:h-10">
          {title}
        </h4>

        {/* Budget */}
        <div className="text-secondary-500 font-bold text-sm sm:text-lg mt-1.5 sm:mt-2 truncate">
          {budget}
        </div>

        {/* Category & Location */}
        <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2 overflow-hidden">
          <span className="bg-secondary-50 text-secondary-600 text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 rounded shrink-0 truncate max-w-[80px] sm:max-w-none">
            {category}
          </span>
          {location && (
            <div className="flex items-center gap-1 text-gray-500 min-w-0">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
              <span className="text-[10px] sm:text-xs truncate">{location}</span>
            </div>
          )}
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-between mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1 text-gray-500">
              <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="text-[10px] sm:text-xs font-medium">{bids}</span>
            </div>
            <div className="flex items-center gap-1 text-gray-500">
              <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="text-[10px] sm:text-xs">{views}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="text-[10px] sm:text-xs">{postedTime}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
